import { useCallback, useLayoutEffect, useState } from "react";

export const CFD_GUIDE_STAGE_ID = "cfd-guide-stage";

/**
 * 返回目标元素在视口中的矩形（getBoundingClientRect），用于 position:fixed。
 *
 * 说明：若用「目标 − #cfd-guide-stage」计算相对坐标，会在首帧出现 stage 仍为
 * 初始 top/left=0 而真实 cfdMain 已就位的情况，导致高亮整体偏移。固定到视口即可与真实控件一致。
 */
export function useCfdGuideStageRect(selector: string | null): {
  top: number;
  left: number;
  width: number;
  height: number;
} | null {
  const [rect, setRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const measure = useCallback(() => {
    if (typeof document === "undefined" || !selector) return;
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) return;
    const tr = el.getBoundingClientRect();
    setRect({
      top: tr.top,
      left: tr.left,
      width: tr.width,
      height: tr.height,
    });
  }, [selector]);

  useLayoutEffect(() => {
    if (!selector) return;
    const el = document.querySelector(selector) as HTMLElement | null;
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const ro =
      typeof ResizeObserver !== "undefined" && el ? new ResizeObserver(() => measure()) : null;
    ro?.observe(el);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      ro?.disconnect();
    };
  }, [measure, selector]);

  return rect;
}
